import { useMemo, useState } from 'react';
import { UserPlus, X } from '@phosphor-icons/react';
import api from '../lib/api';
import { extractErrorMessage } from '../lib/errors';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { useT } from '../i18n/I18nContext';

export default function ProjectMembersPanel({
  project,
  users = [],
  onProjectChange,
  onProjectsChange,
}) {
  const { user } = useAuth();
  const t = useT();
  const [memberEmail, setMemberEmail] = useState('');
  const [addingMember, setAddingMember] = useState(false);
  const [removingMemberId, setRemovingMemberId] = useState(null);
  const [removingPending, setRemovingPending] = useState(null);

  const isOwner = user && project.owner_id === user.id;
  const pendingEmails = project.pending_member_emails || [];

  const usersById = useMemo(() => {
    const m = {};
    for (const u of users) m[u.id] = u;
    return m;
  }, [users]);

  const memberRows = useMemo(() => {
    const ids = project.member_ids || [];
    return ids.map((id) => {
      const u = usersById[id];
      return {
        id,
        email: u?.email || id,
        name: u?.name || '',
        color: u?.color,
        isOwner: id === project.owner_id,
      };
    });
  }, [project.member_ids, project.owner_id, usersById]);

  const initialsOf = (label) =>
    (label || '')
      .split(/\s+/)
      .map((p) => p[0])
      .filter(Boolean)
      .join('')
      .slice(0, 2)
      .toUpperCase() || '?';

  const addMember = async (e) => {
    e.preventDefault();
    const email = memberEmail.trim().toLowerCase();
    if (!email) return;
    setAddingMember(true);
    try {
      const { data } = await api.post(`/projects/${project.id}/members`, { email });
      const queued = (data.pending_member_emails || []).includes(email);
      toast.success(queued
        ? t('members.invitedToast', { email })
        : t('members.addedToast', { email }));
      setMemberEmail('');
      onProjectChange && onProjectChange(data);
      onProjectsChange && onProjectsChange();
    } catch (err) {
      toast.error(extractErrorMessage(err, t('members.addFailedToast')));
    } finally {
      setAddingMember(false);
    }
  };

  const removePending = async (email) => {
    if (!window.confirm(t('members.confirmCancel', { email }))) return;
    setRemovingPending(email);
    try {
      const { data } = await api.delete(
        `/projects/${project.id}/pending-members`,
        { params: { email } },
      );
      toast.success(t('members.cancelledToast'));
      onProjectChange && onProjectChange(data);
      onProjectsChange && onProjectsChange();
    } catch (err) {
      toast.error(extractErrorMessage(err, t('members.cancelFailedToast')));
    } finally {
      setRemovingPending(null);
    }
  };

  const removeMember = async (memberId, label) => {
    if (!window.confirm(t('members.confirmRemove', { label }))) return;
    setRemovingMemberId(memberId);
    try {
      const { data } = await api.delete(`/projects/${project.id}/members/${memberId}`);
      toast.success(t('members.removedToast'));
      onProjectChange && onProjectChange(data);
      onProjectsChange && onProjectsChange();
    } catch (err) {
      toast.error(extractErrorMessage(err, t('members.removeFailedToast')));
    } finally {
      setRemovingMemberId(null);
    }
  };

  return (
    <div className="space-y-2">
      <div className="max-h-64 overflow-y-auto pr-1 space-y-2">
      <ul data-testid="members-list" className="space-y-1">
        {memberRows.length === 0 && (
          <li className="text-xs text-zinc-500">{t('members.none')}</li>
        )}
        {memberRows.map((m) => (
          <li
            key={m.id}
            data-testid={`member-${m.id}`}
            className="flex items-center justify-between text-xs font-mono text-zinc-800 gap-2"
          >
            <span className="flex items-center gap-2 min-w-0">
              <span
                data-testid={`member-avatar-${m.id}`}
                style={m.color ? { backgroundColor: m.color } : undefined}
                className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-semibold text-white ${
                  m.color ? '' : 'bg-zinc-500'
                }`}
              >
                {initialsOf(m.name || m.email)}
              </span>
              <span className="truncate">{m.email}{m.name ? ` · ${m.name}` : ''}</span>
            </span>
            {m.isOwner ? (
              <span className="ml-2 text-[10px] uppercase tracking-wider text-zinc-500">{t('members.ownerBadge')}</span>
            ) : (
              isOwner && (
                <button
                  type="button"
                  data-testid={`member-remove-${m.id}`}
                  onClick={() => removeMember(m.id, m.email)}
                  disabled={removingMemberId === m.id}
                  title={t('members.removeTitle')}
                  className="text-zinc-400 hover:text-red-600 disabled:opacity-50"
                >
                  <X size={14} />
                </button>
              )
            )}
          </li>
        ))}
      </ul>
      {pendingEmails.length > 0 && (
        <ul data-testid="pending-list" className="space-y-1 pt-1">
          {pendingEmails.map((email) => (
            <li
              key={email}
              data-testid={`pending-${email}`}
              className="flex items-center justify-between text-xs font-mono text-zinc-600 gap-2"
            >
              <span className="truncate">{email}</span>
              <span className="ml-2 text-[10px] uppercase tracking-wider text-amber-700">{t('members.pendingBadge')}</span>
              {isOwner && (
                <button
                  type="button"
                  data-testid={`pending-remove-${email}`}
                  onClick={() => removePending(email)}
                  disabled={removingPending === email}
                  title={t('members.cancelTitle')}
                  className="text-zinc-400 hover:text-red-600 disabled:opacity-50"
                >
                  <X size={14} />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      </div>
      {isOwner && (
        <form onSubmit={addMember} className="flex gap-2 pt-1">
          <Input
            data-testid="add-member-input"
            type="email"
            placeholder={t('members.addPlaceholder')}
            value={memberEmail}
            onChange={(e) => setMemberEmail(e.target.value)}
            className="rounded-sm font-mono text-xs h-8"
            disabled={addingMember}
          />
          <Button
            type="submit"
            data-testid="add-member-btn"
            size="sm"
            variant="outline"
            className="rounded-sm h-8"
            disabled={addingMember || !memberEmail.trim()}
          >
            <UserPlus size={14} className="mr-1.5" />
            {addingMember ? t('members.addingBtn') : t('members.addBtn')}
          </Button>
        </form>
      )}
      {isOwner && (
        <p className="text-[11px] text-zinc-500">
          {t('members.helpText')}
        </p>
      )}
    </div>
  );
}
